import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Rocket } from "lucide-react";

export const LaunchCampaignDialog = ({ open, onClose, onLaunched }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ campaign_id: "", secteur: "", zone: "", nombre: 5, sequence_template: "demo" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/campaigns").then(({ data }) => setCampaigns(data.filter((c) => c.statut === "validee")));
    api.get("/sequences/templates").then(({ data }) => setTemplates(data));
  }, [open]);

  const launch = async () => {
    if (!form.campaign_id) { toast.error("Choisissez une campagne validée"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${form.campaign_id}/launch`, { ...form, nombre: parseInt(form.nombre, 10) || 1 });
      toast.success(data.message);
      if (data.ignores_sans_email > 0) toast.info(`${data.ignores_sans_email} prospect(s) ignoré(s) faute d'adresse e-mail`);
      onLaunched?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="launch-campaign-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Rocket size={18} /> Lancer une campagne (MODE TEST)</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Campagne commerciale (validée)</Label>
            <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="launch-campaign-select"><SelectValue placeholder="Choisir une campagne" /></SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
              </SelectContent>
            </Select>
            {campaigns.length === 0 && <p className="mt-1 text-xs text-destructive">Aucune campagne validée. Validez d'abord une campagne.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Secteur d'activité (filtre)</Label>
              <Input className="mt-1.5" value={form.secteur} onChange={(e) => setForm({ ...form, secteur: e.target.value })} placeholder="Ex : Hôtellerie" data-testid="launch-sector-input" />
            </div>
            <div>
              <Label>Zone géographique (filtre)</Label>
              <Input className="mt-1.5" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Ville ou département" data-testid="launch-zone-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nombre de prospects</Label>
              <Input type="number" min="1" max="100" className="mt-1.5" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="launch-count-input" />
              <p className="mt-1 text-xs text-muted-foreground">Plafonné au volume quotidien de la campagne</p>
            </div>
            <div>
              <Label>Modèle de séquence</Label>
              <Select value={form.sequence_template} onValueChange={(v) => setForm({ ...form, sequence_template: v })}>
                <SelectTrigger className="mt-1.5" data-testid="launch-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-900">
            MODE TEST : les e-mails sont générés par l'IA et enregistrés avec la séquence complète (J0 + 3 relances), mais aucun e-mail réel n'est expédié. Les relances s'arrêtent immédiatement dès qu'une réponse est enregistrée.
          </div>
          <Button onClick={launch} disabled={busy} className="w-full bg-accent hover:bg-accent/90" data-testid="launch-submit-button">
            {busy ? "Lancement…" : "Lancer la prospection"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
